import type { Column, IRowNode } from 'ag-grid-community';

// Columns that hold no copyable text (icons / action buttons / expand toggle).
// Skipped when copying a cell or a whole row to the clipboard.
export const COPY_SKIP_COL_IDS = new Set<string>([
  'expand',
  'evidence',
  'actions',
]);

// Turn an arbitrary cell value into a clipboard-friendly string. Handles the
// array-of-objects shape used by the contributions column, plain arrays, and
// scalars; null/undefined become an empty string.
export const formatCellForCopy = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object' && 'contribution' in item) {
          return String((item as { contribution: unknown }).contribution ?? '');
        }
        return formatCellForCopy(item);
      })
      .filter(Boolean)
      .join(', ');
  }
  return String(value);
};

// Minimal slice of the AG Grid API used when building copy text. Declaring it
// here (rather than depending on the full GridApi) keeps buildCopyPayload easy
// to unit test with a small mock.
export interface CopyGridApiLike<TData> {
  getAllDisplayedColumns(): Column[];
  getCellValue(params: {
    rowNode: IRowNode<TData>;
    colKey: string | Column;
  }): unknown;
}

export interface CopyPayload {
  /** Text written to the clipboard. */
  text: string;
  /** Column ids to flash as visual confirmation. */
  flashColumns: string[];
  /** Message shown in the transient popup. */
  feedback: string;
}

// Build the clipboard payload for either a single cell or a whole row. Returns
// null when there is nothing copyable (e.g. a single icon/action column).
export const buildCopyPayload = <TData>(
  api: CopyGridApiLike<TData>,
  rowNode: IRowNode<TData>,
  column: Column,
  copyWholeRow: boolean
): CopyPayload | null => {
  if (copyWholeRow) {
    // Whole-row copy: every visible, text-bearing column, tab-separated so it
    // pastes cleanly into a spreadsheet.
    const columns = api
      .getAllDisplayedColumns()
      .filter((col) => !COPY_SKIP_COL_IDS.has(col.getColId()));
    const text = columns
      .map((col) => formatCellForCopy(api.getCellValue({ rowNode, colKey: col })))
      .join('\t');
    return {
      text,
      flashColumns: columns.map((col) => col.getColId()),
      feedback: 'Row copied to clipboard',
    };
  }

  if (COPY_SKIP_COL_IDS.has(column.getColId())) {
    return null;
  }
  const text = formatCellForCopy(api.getCellValue({ rowNode, colKey: column }));
  return {
    text,
    flashColumns: [column.getColId()],
    feedback: 'Cell copied to clipboard',
  };
};

// Write text to the clipboard, falling back to a hidden-textarea + execCommand
// for non-secure contexts where navigator.clipboard is unavailable. Resolves to
// whether the copy succeeded.
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};
